import type { Doc } from "building-types/doc";
import type { ViewHandle } from "building-types/handles/viewHandleTypes";
import { BuiltinPlanViewType } from "building-types/views/BuiltinPlanViewType";
import type { PlanView, PlanViewLevelMember } from "building-types/views/View3D";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import { docGetTableEntity } from "./entity/docGetTableEntity";

/** True when a plan view is a single-level floor plan for the given level. */
function planViewIsFloorPlanForLevel(planView: PlanView, levelId: string): boolean {
  return (
    planView.referenceType === BuiltinPlanViewType.FLOOR &&
    planView.levelIds.length === 1 &&
    planView.levelIds[0].entityId === levelId
  );
}

export function docHasPlanViewForLevel(doc: Doc, levelId: string): boolean {
  return Object.values(doc.planViews).some((view) => planViewIsFloorPlanForLevel(view, levelId));
}

export function getPlanViewLevelIds(doc: Doc, view: PlanView): readonly PlanViewLevelMember[] {
  if (view.planViewType !== "COMPOSITE") {
    return view.levelIds;
  }

  const [compositeLevelId] = view.levelIds;
  const compositeLevel = docGetTableEntity(doc, { type: "COMPOSITE_LEVEL", id: compositeLevelId.entityId });
  assertIsNotNullOrUndefined(compositeLevel);
  return compositeLevel.members;
}

/** Level entity ids visible in a plan view (composite views expand to member levels). */
export function planViewLevelIdSet(doc: Doc, planView: PlanView): ReadonlySet<string> {
  const members = getPlanViewLevelIds(doc, planView);
  return new Set(members.map((member) => member.entityId));
}

export function isPlanViewForCompositeLevel(planView: PlanView, compositeLevelId: string) {
  return planView.planViewType === "COMPOSITE" && planView.levelIds[0].entityId === compositeLevelId;
}

export function isViewHandleForCompositeLevel(
  doc: Doc,
  viewHandle: ViewHandle | undefined,
  compositeLevelId: string,
): boolean {
  if (!viewHandle || viewHandle.type !== "PLAN_VIEW") {
    return false;
  }

  const planView = docGetTableEntity(doc, viewHandle);
  if (!planView) {
    return false;
  }

  return isPlanViewForCompositeLevel(planView, compositeLevelId);
}
